import React, { createContext, useContext, useEffect, useState } from 'react';
import { StatusCode } from '../../components/Alert.tsx';
import StoresController from '../../controllers/StoresController.ts';
import { BaseSearchParam } from '../../models/searchParams/baseSearchParam';
import { TableSearchParam } from '../../models/searchParams/tableSearchParam';
import { Store } from '../../models/stores/store.ts';
import { useAlert } from '../alertProvider.tsx';

type StoresContextType = {
    isLoaded?: boolean;
    stores: Store[];
    totalCount: number;
    tableSearchParam: TableSearchParam;
    setTableSearchParam: (tableSearchParam: TableSearchParam) => void;
    searchParam: BaseSearchParam;
    setSearchParam: (baseSearchParam: BaseSearchParam) => void;
    deleteStore: (storeID: number) => {};
};

const StoresContext = createContext<StoresContextType>({} as StoresContextType);

export const StoresContextProvider = ({ children }: { children: any }) => {
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [stores, setStores] = useState<Store[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [tableSearchParam, setTableSearchParam] = useState<TableSearchParam>({ offset: 0, limit: 25 });
    const [searchParam, setSearchParam] = useState<BaseSearchParam>({} as BaseSearchParam);
    const { showAlert } = useAlert();

    const fetchStores = async () => {
        try {
            setIsLoaded(false);
            const result = await StoresController.get(tableSearchParam, searchParam);
            setStores(result.stores);
            setTotalCount(result.totalCount);
        } catch (error) {
            console.error('Error fetching stores:', error);
        } finally {
            setIsLoaded(true);
        }
    };
    useEffect(() => {
        fetchStores();
    }, [tableSearchParam, searchParam]);

    const deleteStore = async (storeID: number) => {
        try {
            const result = await StoresController.delete(storeID);
            if (result.notificationStatus == StatusCode.success)
                fetchStores();
            else
                showAlert(result.notificationStatus, result.notificationMessage);
        } catch (error) {
            console.error('Error deleting store:', error);
        } finally {
            setIsLoaded(true);
        }
    }

    const context: StoresContextType = {
        isLoaded,
        stores,
        totalCount,
        tableSearchParam,
        setTableSearchParam,
        searchParam,
        setSearchParam,
        deleteStore
    }

    return (
        <StoresContext.Provider value={context}>
            {children}
        </StoresContext.Provider>
    )
}

export const useStoresContext = () => useContext(StoresContext)
